import { useStore } from "@nanostores/preact"
import { useCallback, useEffect, useRef, useState } from "preact/hooks"
import { JSXInternal } from "preact/src/jsx"

import { TranslationProvider } from "./components/Translation"
import { WrapperHeader } from "./components/WrapperHeader"
import { AlreadySubscribed } from "./screens/AlreadySubscribedScreen"
import { SignIn } from "./screens/SignInScreen"
import { SignUp } from "./screens/SignUpScreen"
import { EmailVerify } from "./screens/EmailVerifyScreen"
import { Loading } from "./screens/LoadingScreen"
import { ErrorScreen } from "./screens/ErrorScreen"
import { ForgotPassword } from "./screens/ForgotPasswordScreen"
import { Payment } from "./screens/PaymentScreen"
import { Success } from "./screens/SuccessScreen"
import { EmailVerificationSuccess } from "./screens/EmailVerificationSuccessScreen"
import { SocialAuthSuccess } from "./screens/SocialAuthSuccessScreen"
import { getSupport } from "./services/backend"
import { initializePromoFromLocation } from "./services/promo-code"
import { Screens } from "./services/screens"
import { profile, support as supportStore } from "./services/store"
import { Support } from "./services/support"

import css from "./style.module.scss"

const meta = document.querySelector("meta[name=csrf-token]") as HTMLMetaElement
const token = meta ? meta.content : ""

export function Modal({
  action,
  source,
  onClose,
  onReload,
  variant = "modal",
}: {
  action: Screens
  source: string
  onClose?: () => void
  onReload: (action: Screens) => void
  variant?: "modal" | "page"
}) {
  const [errorMessage, setErrorMessage] = useState("")

  const [history, setHistory] = useState<Screens[]>([])
  const [page, setPage] = useState(Screens.LoadingScreen)
  const support = useStore(supportStore)
  const pageRef = useRef(page)
  const socialAuthDelayMs = 900

  const applySupportData = useCallback(
    (data: Support) => {
      supportStore.set(data)
      profile.setKey("source", source)
      profile.setKey("premium", data.user.premium)
      profile.setKey("legacy12day", data.user.legacy12day)
      profile.setKey("authorized", data.user.signedIn)
      profile.setKey("countryIso", data.default_iso_country)
      profile.setKey("emailVerified", data.user.emailVerified)
      profile.setKey("email", data.user.email)
      profile.setKey("paymentProfile", data.user.paymentProfile || null)
      profile.setKey("premiumExpires", data.user.premiumExpires || null)
      if (data.csrf_token) {
        const csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
        if (csrfMeta) csrfMeta.setAttribute("content", data.csrf_token)
      }
    },
    [source],
  )

  const popHistory = () => {
    setHistory(history.slice(0, -1))
    setPage(history[history.length - 1])
  }

  const updatePage = (newPage: Screens) => {
    setHistory(history.concat(page))
    setPage(newPage)
  }

  useEffect(() => {
    initializePromoFromLocation()
  }, [])

  useEffect(() => {
    setErrorMessage("")
    ;(async () => {
      let data: Support
      try {
        data = await getSupport()
      } catch (error) {
        let msg = "fetching support, but the error is unknown"
        if (error instanceof Error) msg = error.message

        setPage(Screens.ErrorScreen)
        setErrorMessage(msg)

        return
      }

      applySupportData(data)
      // console.log(data)

      // If the requested action is email verification, respect that regardless of auth status
      if (action === Screens.EmailVerifyScreen) {
        setPage(Screens.EmailVerifyScreen)
      } else if (data.user.signedIn) {
        setPage(Screens.PaymentScreen)
      } else {
        setPage(Screens.SignUpScreen)
      }
    })()
  }, [action, applySupportData, source])

  useEffect(() => {
    pageRef.current = page
  }, [page])
  useEffect(() => {
    ;(window as any).signupPopupOpen = true
    let redirectTimeout: number | null = null
    let cancelled = false

    const handleSocialAuthSuccess = async () => {
      const originPage = pageRef.current
      setHistory((prev) => prev.concat(originPage))
      setPage(Screens.SocialAuthSuccessScreen)
      profile.setKey("reloadRequired", true)
      try {
        const data = await getSupport()
        applySupportData(data)
        if (data.user.premium) {
          setPage(Screens.AlreadySubscribedScreen)
          return
        }
      } catch (error) {
        // fall back to payment screen if support fetch fails
      }

      redirectTimeout = window.setTimeout(() => {
        if (cancelled) return
        setPage(Screens.PaymentScreen)
      }, socialAuthDelayMs)
    }

    window.addEventListener("social-auth:success", handleSocialAuthSuccess as EventListener)

    return () => {
      cancelled = true
      if (redirectTimeout) {
        window.clearTimeout(redirectTimeout)
      }
      window.removeEventListener("social-auth:success", handleSocialAuthSuccess as EventListener)
      ;(window as any).signupPopupOpen = false
    }
  }, [action, applySupportData, onReload, socialAuthDelayMs])

  const reloadPage = () => {
    if (action === Screens.ForgotPasswordScreen) {
      window.location.assign("/")
    } else {
      window.location.reload()
    }
  }
  const closePage = () => {
    if (profile.get().reloadRequired) {
      reloadPage()
    } else if (onClose) {
      onClose()
    }
  }

  const closeSuccessPage = () => {
    const user = profile.get()
    if (!user.emailVerified) {
      // Don't allow closing if email is not verified
      return
    }
    if (onClose) {
      closePage()
    }
  }

  const renderWithHeader = (
    content: JSXInternal.Element,
    headerProps?: {
      onClose?: () => void
      onBack?: () => void
      tooltip?: string
    },
  ) => {
    if (variant === "page") {
      return content
    }
    return (
      <WrapperHeader
        onClose={headerProps?.onClose}
        onBack={headerProps?.onBack}
        tooltip={headerProps?.tooltip}
      >
        {content}
      </WrapperHeader>
    )
  }

  let el: JSXInternal.Element
  switch (page) {
    case Screens.LoadingScreen:
      el = renderWithHeader(<Loading />, {
        onClose: closePage,
      })
      break
    case Screens.ErrorScreen:
      el = renderWithHeader(<ErrorScreen message={errorMessage} onReload={reloadPage} />, {
        onClose: closePage,
      })
      break
    case Screens.SignUpScreen:
      el = renderWithHeader(
        <SignUp
          token={token}
          isoCountries={support.iso_countries}
          onAuthenticate={() => updatePage(Screens.PaymentScreen)}
          onPageUpdate={updatePage}
        />,
        { onClose: closePage, tooltip: "1/3" },
      )
      break
    case Screens.SignInScreen:
      el = renderWithHeader(
        <SignIn
          token={token}
          onAuthenticate={() => updatePage(Screens.PaymentScreen)}
          onPasswordForgot={() => updatePage(Screens.ForgotPasswordScreen)}
          onPageUpdate={updatePage}
        />,
        { onClose: closePage, onBack: popHistory, tooltip: "1/3" },
      )
      break
    case Screens.ForgotPasswordScreen:
      el = renderWithHeader(
        <ForgotPassword token={token} onPasswordReset={() => setPage(Screens.SignInScreen)} />,
        { onBack: popHistory },
      )
      break
    case Screens.AlreadySubscribedScreen:
      el = renderWithHeader(
        <AlreadySubscribed currencies={support.currencies} onClose={() => onReload(action)} />,
        { onClose: closePage },
      )
      break
    case Screens.PaymentScreen:
      el = renderWithHeader(
        <Payment
          token={token}
          products={support.products}
          currencies={support.currencies}
          isoCountries={support.iso_countries}
          publishableKey={support.stripe_publishable_key}
          features={support.features}
          onPay={() => {
            updatePage(Screens.SuccessScreen)
          }}
        />,
        {
          onClose: closePage,
          onBack: popHistory,
          tooltip:
            history.includes(Screens.SignUpScreen) || history.includes(Screens.SignInScreen)
              ? "2/3"
              : "1/2",
        },
      )
      break
    case Screens.EmailVerifyScreen:
      el = renderWithHeader(
        <EmailVerify
          token={token}
          onVerify={() => updatePage(Screens.EmailVerificationSuccessScreen)}
        />,
        { onClose: closePage, onBack: history.length ? popHistory : undefined },
      )
      break
    case Screens.SuccessScreen:
      el = renderWithHeader(
        <Success
          onClose={onClose ? closeSuccessPage : undefined}
          onEmailVerify={() => updatePage(Screens.EmailVerifyScreen)}
        />,
        {
          onClose: closeSuccessPage,
          tooltip:
            history.includes(Screens.SignUpScreen) || history.includes(Screens.SignInScreen)
              ? "3/3"
              : "2/2",
        },
      )
      break
    case Screens.EmailVerificationSuccessScreen:
      el = renderWithHeader(
        <EmailVerificationSuccess onClose={onClose ? closePage : undefined} />,
        { onClose: closePage },
      )
      break
    case Screens.SocialAuthSuccessScreen:
      el = renderWithHeader(<SocialAuthSuccess />, { onClose: closePage })
      break
    default:
      throw new Error(`Unexpected error. Specified page (${page}) is not supported.`)
  }

  if (variant === "page") {
    return (
      <TranslationProvider definitions={support?.messages}>
        <div className={css["page-container"]}>{el}</div>
      </TranslationProvider>
    )
  }

  return (
    <TranslationProvider definitions={support?.messages}>
      <div
        onClick={(event) => {
          if ((event.target as HTMLElement) === (event.currentTarget as HTMLElement)) {
            if (page === Screens.SuccessScreen) {
              closeSuccessPage()
            } else {
              closePage()
            }
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            if (page === Screens.SuccessScreen) {
              closeSuccessPage()
            } else {
              closePage()
            }
          }
        }}
        role="button"
        tabIndex={0}
        className={css.backdrop}
      >
        {el}
      </div>
    </TranslationProvider>
  )
}
